import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Input,
} from "@mui/material";
import { Typography, TextField } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import FormHelperText from "@mui/material/FormHelperText";
import { Add } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { EntityService, searchService } from "../../services";
import SearchForm from "./SearchForm";
import Loading from "../utils/Loading";
import { useDispatch, useSelector } from "react-redux";
import OrderEntities from "../../components/entities/OrderEntities";
import MyMapContainer from "../../components/geoloc/MyMapContainer";
import clsx from "clsx";
import { saveInit } from "../../reducers/entity/entitySlice";

const PREFIX = "JourneyForm";

const classes = {
  root: `${PREFIX}-root`,
  paper: `${PREFIX}-paper`,
  form: `${PREFIX}-form`,
  marginBottom: `${PREFIX}-marginBottom`,
  myDesignImageContainer: `${PREFIX}-myDesignImageContainer`,
  paintingContainer: `${PREFIX}-paintingContainer`,
  uploadButton: `${PREFIX}-uploadButton`,
  uploadField: `${PREFIX}-uploadField`,
  FirstimageContainer: `${PREFIX}-FirstimageContainer`,
  imageContainer: `${PREFIX}-imageContainer`,
  grid: `${PREFIX}-grid`,
  gridList: `${PREFIX}-gridList`,
  tileWidth: `${PREFIX}-tileWidth`,
  title: `${PREFIX}-title`,
  titleBar: `${PREFIX}-titleBar`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    height: "100vh",
  },

  [`&.${classes.paper}`]: {
    padding: theme.spacing(2, 3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      minWidth: "540px",
    },
    margin: "0 auto",
  },

  [`& .${classes.form}`]: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
    position: "relative",
    top: 0,
  },

  [`& .${classes.marginBottom}`]: {
    width: "100%",
    marginBottom: "1.5rem",
  },

  [`& .${classes.myDesignImageContainer}`]: {
    width: "100%",
    maxWidth: "300px",
    objectFit: "cover",
    margin: "0",
  },

  [`& .${classes.paintingContainer}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.uploadButton}`]: {
    height: "50px",
    marginTop: "1rem",
  },

  [`& .${classes.uploadField}`]: {
    maxWidth: "600px",
    textAlign: "left",
    marginBottom: "1rem",
    display: "flex",
    flexDirection: "row",
  },

  [`& .${classes.FirstimageContainer}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.imageContainer}`]: {
    marginTop: "2rem",
  },

  [`& .${classes.grid}`]: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    overflow: "hidden",
    padding: "0.5rem",
    minHeight: "200px",
  },

  [`& .${classes.gridList}`]: {
    flexWrap: "nowrap",
    display: "flex",
    height: "215px",
    overflow: "auto",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
    // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
    transform: "translateZ(0)",
  },

  [`& .${classes.tileWidth}`]: {
    width: "inherit",
    maxHeight: "100%",
    height: "auto",
    height: "150px",
    objectFit: "contain",
  },

  [`& .${classes.title}`]: {
    color: theme.palette.primary.light,
    fontSize: "0.8rem",
  },

  [`& .${classes.titleBar}`]: {
    position: "inherit",
    background:
      "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
    "& > div": {
      marginLeft: "5px",
    },
  },
}));

function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}

export default function JourneyForm(props) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });
  const { t } = useTranslation();

  const debouncedSearchName = useDebounce(watch("name"), 300);
  const [isNameUsed, setIsNameUsed] = useState(null);
  const [isNameSearching, setIsNameSearching] = useState(false);
  const [url, setUrl] = React.useState(null);
  const [urlHeader, setUrlHeader] = React.useState(null);
  const watchEntityImage = watch("logo");
  const watchEntityHeader = watch("header");
  const [selectedExpariences, setSelectedExpariences] = useState([]);
  const [searchResultExp, setSearchResultExp] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEntity, setLoadingEntity] = useState(false);
  const save = useSelector((state) => state.entity?.save);
  const dispatch = useDispatch();
  const [entity, setEntity] = React.useState(null);
  const [entityVisible, setEntityVisible] = useState(false);
  const [isSecuredByPassword, setIsSecuredByPassword] = useState(false);
  const [password, setPassword] = useState(null);
  const [positions, setPositions] = useState([]);

  function handleImageReader(event) {
    let tmp = URL.createObjectURL(event.target.files[0]);
    setUrl(tmp);
  }

  function handleHeaderReader(event) {
    let tmp = URL.createObjectURL(event.target.files[0]);
    setUrlHeader(tmp);
  }

  useEffect(() => {
    const tmpExpariences = [...selectedExpariences];
    const tmpPositions = [];
    tmpExpariences.forEach((exp) => {
      exp.positions.forEach((pos) => {
        const { radius, title, type, visible, ...rest } = pos.position;
        tmpPositions.push({
          radius,
          title,
          displayName: exp.displayName,
          type,
          visible,
          lat: rest.coordinates[0],
          long: rest.coordinates[1],
        });
      });
    });
    setPositions(tmpPositions);
  }, [selectedExpariences]);

  useEffect(() => {
    if (props.entityId) {
      setLoadingEntity(true);
      EntityService.getFullEntity(props.entityId)
        .then((result) => {
          setEntity(result);
          setEntityVisible(result.visible);
          setIsSecuredByPassword(result.isSecuredByPassword);
          setPassword(result.password);
          setSelectedExpariences([...formatValues(result.expariences, "xp")]);
        })
        .finally(() => setLoadingEntity(false));
    }
  }, []);

  useEffect(() => {
    dispatch(saveInit());
    if (save) {
      handleSubmit(onSubmit)();
    }
  }, [save]);

  const formatValues = (data, type) => {
    return data.map((d) => ({
      ...d,
      type,
    }));
  };

  useEffect(() => {
    if (debouncedSearchName && debouncedSearchName !== entity?.name) {
      setIsNameSearching(true);
      EntityService.exists(debouncedSearchName).then((results) => {
        setIsNameSearching(false);
        // Filter out results
        setIsNameUsed(results.exists);
      });
    }
  }, [debouncedSearchName]);

  function onSubmit(data) {
    props.save({
      ...data,
      logo: entity?.logo,
      header: entity?.header,
      visible: entityVisible,
      type: "journey",
      isSecuredByPassword:
        isSecuredByPassword && data.password && data.password.length > 0
          ? true
          : false,
      password: data.password ? data.password : password ? password : "",
      expariences: selectedExpariences.map((xp) => xp.id),
      logoFile: watch("logo"),
      headerFile: watch("header"),
    });
  }

  async function search(value) {
    if (value && value.length > 0) {
      setLoading(true);
      const resultExpariences = await searchService.searchExpariences(value);
      setSearchResultExp(
        resultExpariences.filter(
          (r) => !selectedExpariences.find((md) => md.id === r.id)
        )
      );
      setLoading(false);
    } else {
      setSearchResultExp([]);
    }
  }

  async function _delete(object) {
    setSelectedExpariences((oldValue) =>
      oldValue.filter((md) => md._id !== object._id)
    );
    setSearchResultExp((oldValue) => [object, ...oldValue]);
  }

  async function addExparience(exparience) {
    setSelectedExpariences((oldValue) => [
      ...oldValue,
      { ...exparience, type: "xp" },
    ]);
    setSearchResultExp((oldValue) =>
      oldValue.filter((md) => md._id !== exparience._id)
    );
  }

  return loadingEntity ? (
    <Loading />
  ) : (
    <Root>
      <div className={classes.paper}>
        <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
          {entity && (
            <Input
              type="hidden"
              name="_id"
              id="_id"
              defaultValue={entity?.id || null}
              // inputRef={register()}
            />
          )}
          <FormControl variant="outlined" className={classes.marginBottom}>
            <InputLabel htmlFor="name">{t("journeys.name")}</InputLabel>
            <OutlinedInput
              required
              fullWidth
              name="name"
              id="me"
              type="text"
              label={t("journeys.name")}
              error={errors.hasOwnProperty("name") || isNameUsed}
              autoComplete="name"
              {...register("name", { required: true })}
              endAdornment={
                (isNameUsed !== null || isNameSearching) && (
                  <InputAdornment position="end">
                    <IconButton aria-label="email availability" size="large">
                      {isNameSearching ? (
                        <CircularProgress />
                      ) : isNameUsed ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }
              defaultValue={entity?.name || ""}
            />
            {isNameUsed && (
              <FormHelperText id="name" error>
                {t("journeys.errorMessages.name")}
              </FormHelperText>
            )}
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                color={"primary"}
                onChange={(e) => setEntityVisible(e.target.checked)}
                checked={entityVisible}
              />
            }
            label={t("journeys.entityVisibile")}
          />
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  color={"primary"}
                  onChange={(e) => setIsSecuredByPassword(e.target.checked)}
                  checked={isSecuredByPassword}
                />
              }
              style={{ marginBottom: "0.5rem" }}
              label={t("journeys.securedByPasswordTick")}
            />
            {isSecuredByPassword && (
              <React.Fragment>
                <TextField
                  label={t("journeys.password")}
                  name="password"
                  defaultValue={password}
                  error={errors.hasOwnProperty("password")}
                  fullWidth
                  variant={"outlined"}
                  {...register("password", {
                    validate: (value) =>
                      isSecuredByPassword && value.length > 0
                        ? null
                        : t("journeys.passwordTicked"),
                  })}
                />
                <FormHelperText
                  id="password"
                  error
                  className={clsx(classes.marginBottom)}
                >
                  {errors["password"] && errors["password"].message}
                </FormHelperText>
              </React.Fragment>
            )}
          </div>
          <div className={classes.FirstimageContainer}>
            <Typography variant="h6">{t("journeys.logo")}</Typography>
            <div className={classes.uploadField}>
              <div className={classes.paintingContainer}>
                {(url || (entity && entity.logo)) && (
                  <img
                    id="entityImage"
                    className={classes.myDesignImageContainer}
                    alt={watchEntityImage ? "entity logo" : null}
                    src={url ? url : entity.logo}
                  />
                )}
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    component="label"
                    className={classes.uploadButton}
                    onChange={(e) => handleImageReader(e)}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {t("myDesign.zones.uploadVideo.selectFileButton")}
                    <input
                      type="file"
                      accept="image/*"
                      name="logo"
                      {...register("logo")}
                      hidden
                    />
                  </Button>
                  <Typography variant="body1">
                    {watchEntityImage && watchEntityImage.length > 0
                      ? watchEntityImage[0].name
                      : ""}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
          <div className={classes.imageContainer}>
            <Typography variant="h6">{t("journeys.header")}</Typography>
            <div className={classes.uploadField}>
              <div className={classes.paintingContainer}>
                {(urlHeader || (entity && entity.header)) && (
                  <img
                    id="entityImage"
                    className={classes.myDesignImageContainer}
                    alt={watchEntityHeader ? "entity header" : null}
                    src={urlHeader ? urlHeader : entity.header}
                  />
                )}
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    component="label"
                    className={classes.uploadButton}
                    onChange={(e) => handleHeaderReader(e)}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {t("myDesign.zones.uploadVideo.selectFileButton")}
                    <input
                      type="file"
                      accept="image/*"
                      name="header"
                      {...register("header")}
                      hidden
                    />
                  </Button>
                  <Typography variant="body1">
                    {watchEntityHeader && watchEntityHeader.length > 0
                      ? watchEntityHeader[0].name
                      : ""}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </form>
        <div className={classes.marginBottom}>
          {selectedExpariences.length > 0 && (
            <React.Fragment>
              <h3>{t("journeys.expariences")}</h3>
              <OrderEntities
                id="expariences"
                elements={selectedExpariences}
                deleteTile={_delete}
                setOrder={setSelectedExpariences}
              />
            </React.Fragment>
          )}
          <div style={{ marginBottom: "1rem" }} />
          <SearchForm
            searchValue={""}
            search={search}
            searchText={t("journeys.search")}
            loading={loading}
          />
          {!loading && (
            <div>
              {searchResultExp && searchResultExp.length > 0 && (
                <div>
                  <h3>{t("journeys.expariences")}</h3>
                  <div className={classes.grid}>
                    <div className={classes.gridList} spacing={12}>
                      {searchResultExp.map((tile) => {
                        return (
                          <div
                            key={tile._id}
                            style={{ width: "150px", marginRight: "0.5rem" }}
                          >
                            <img
                              src={tile.logoUrl}
                              alt={tile.name}
                              className={classes.tileWidth}
                            />
                            <ImageListItemBar
                              title={tile.name}
                              classes={{
                                root: classes.titleBar,
                                title: classes.title,
                              }}
                              actionIcon={
                                <IconButton
                                  variant="contained"
                                  color="primary"
                                  aria-label={`add ${tile.name}`}
                                  onClick={() => addExparience(tile)}
                                  size="small"
                                >
                                  <Add className={classes.title} />
                                </IconButton>
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {positions.length > 0 && (
            <div style={{ marginTop: "2rem" }}>
              <MyMapContainer
                positions={positions}
                visualize
                setDatesDisable={() => console.log("no dates")}
              />
            </div>
          )}
        </div>
      </div>
    </Root>
  );
}
